import { memo, useCallback, ReactNode } from 'react'
import cn from 'clsx'

// Components
import { Link } from '@tracklib/kit'
import Artists from 'components/track/artists'
import Buttons from 'components/track/buttons'

// Utils
import { renderLink, renderTitle } from 'components/player/info'

// Types
import { Loop, Song, Track } from 'types'

import styles from './header.css'

type HeaderProps = {
  song: Song
  track: Track
  loop: Loop | null | undefined
  hideButtons?: boolean
  renderSpecificTrackNames?: boolean
  renderOnlySpecificTrackName?: boolean
  children?: JSX.Element | boolean
  mode?: string
}

const Header = ({
  song,
  track,
  loop,
  hideButtons,
  renderSpecificTrackNames,
  renderOnlySpecificTrackName,
  children,
  mode,
}: HeaderProps): JSX.Element => {
  const trackId = track?.id
  const trackSlug = track?.slug
  const songSlug = song?.slug
  const getShareUrl = useCallback(() => {
    let loopString = ''
    if (loop) {
      const { time_start, time_end, beat_start, beat_count } = loop
      loopString = `?shared_loop=${trackId}:${time_start || ''}:${time_end || ''}:${beat_start}:${beat_count}`
    }

    return `/song/${songSlug}/${loopString}${
      trackSlug !== 'master' && trackSlug !== null && trackSlug !== undefined ? `#${trackSlug}` : ''
    }`
  }, [JSON.stringify(loop), songSlug, trackSlug, trackId]) // eslint-disable-line

  const shareUrl = getShareUrl()

  const renderTrackCount = (): ReactNode => {
    const tracksCount = track.track_count
    if (tracksCount > 1 && !renderOnlySpecificTrackName) {
      if (track.types.some(x => x === 17) && !renderSpecificTrackNames) {
        return <span>{tracksCount} multi-tracks</span>
      }
      return `+${tracksCount - 1} ${tracksCount - 1 > 1 ? 'multi-tracks' : 'multi-track'}`
    }
    return ''
  }

  const showTrackCount = renderTrackCount() !== ''
  if (mode === 'compact') {
    return (
      <header className={styles.wrapper}>
        <div
          className={cn(
            renderSpecificTrackNames ? styles.titleAndArtistCompactBig : styles.titleAndArtistCompact,
            showTrackCount && styles.padTrackCount,
          )}
        >
          <Link to={renderLink(song, track)} className={styles.title} title={renderTitle(song, track, renderSpecificTrackNames)}>
            <em>{renderTitle(song, track, renderSpecificTrackNames, renderOnlySpecificTrackName)}</em>
          </Link>
          <Artists artists={song.artists} genres={song.genres} />
        </div>
        <div className={renderSpecificTrackNames ? styles.hidden : styles.songLinkTracksCompactWrapper}>
          {showTrackCount && <span className={styles.songLinkTracks}>{renderTrackCount()}</span>}
        </div>
        {children}
        {!hideButtons && <Buttons trackId={trackId} loop={loop} shareUrl={shareUrl} mode={mode} />}
      </header>
    )
  }

  return (
    <header className={styles.wrapper}>
      <div className={cn(styles.titleAndArtist, showTrackCount && styles.padTrackCount)}>
        <Link to={renderLink(song, track)} className={styles.title} title={renderTitle(song, track, renderSpecificTrackNames)}>
          <em>{renderTitle(song, track, renderSpecificTrackNames, renderOnlySpecificTrackName)}</em>
          {showTrackCount && <span className={styles.songLinkTracks}>{renderTrackCount()}</span>}
        </Link>
        <Artists artists={song.artists} genres={song.genres} />
      </div>
      {!hideButtons && <Buttons trackId={trackId} loop={loop} shareUrl={shareUrl} mode={mode} />}
    </header>
  )
}

export default memo(Header)
